'use client'

import { useEffect } from 'react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useMutation, useQuery } from '@apollo/client'
import { SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import LanguageSwitcher from '@/components/shared/languageSelect'
import { useLanguage } from '@/context/LanguageContext'
import '@/components/styles/Form.css'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import {
  GET_ADVANCED_PRODUCT_DETAILS,
  UPDATE_ADVANCED_PRODUCT_PROFILES,
} from '../productQueries'
import { GET_ADMIN_SETTINGS } from '@/containers/Settings/adminSettingsQueries'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import { Separator } from '@radix-ui/react-separator'
import { Divide } from 'lucide-react'

const formSchema = z.object({
  reference: z.string().optional(),
  advanced_product_profiles: z
    .array(
      z.object({
        id: z.number().optional(),
        name: z.string().min(1, 'Name is required'),
        long_product_name: z.string().optional(),
        original_product_name: z.string().optional(),
        description: z.string().optional(),
        language: z.string(),
        meta_information: z.object({
          id: z.number().optional(),
          permalink: z.string().min(1, 'Permalink is required'),
          meta_title: z.string().optional(),
          meta_description: z.string().optional(),
        }),
      })
    )
    .transform((items, ctx) => {
      const filteredItems = items.filter(item => item.name?.trim() !== '')
      if (filteredItems.length === 0) {
        items.forEach((item, index) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one language profile needs to be filled',
            path: [`[${index}].name`],
          })
        })
      }
      return filteredItems
    }),
})

interface AdvancedProductFormProps {
  data: {
    id: string
    type: string
  }
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose?: () => void
}

export default function AdvancedProductForm({
  data,
  onDirtyChange,
  onRequestClose,
}: AdvancedProductFormProps) {
  const { allLanguages, languageId, language } = useLanguage()
  const id = Number(data?.id)

  const {
    data: { findUniqueProductContainer: productData } = {},
    loading: loadingProductData,
  } = useQuery(GET_ADVANCED_PRODUCT_DETAILS, {
    variables: {
      id,
    },
    fetchPolicy: 'network-only',
    skip: !id,
  })

  const { data: adminSettingsData } = useQuery(GET_ADMIN_SETTINGS)

  const adminSettings = adminSettingsData?.findManySetting || []
  const descriptionTranslateAction = adminSettings.find(
    setting => setting.name === 'admin_product_description_translate_action'
  )?.value
  const nameTranslateAction = adminSettings.find(
    setting => setting.name === 'admin_product_name_translate_action'
  )?.value

  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      reference: '',
      advanced_product_profiles: allLanguages.map(lang => ({
        id: undefined,
        name: '',
        long_product_name: '',
        original_product_name: '',
        description: '',
        language: lang,
        meta_information: {
          id: undefined,
          permalink: '',
          meta_title: '',
          meta_description: '',
        },
      })),
    },
  })

  console.log(
    'form errors',
    form.formState.errors,
    form.formState.touchedFields
  )

  const [updateProduct] = useMutation(UPDATE_ADVANCED_PRODUCT_PROFILES, {
    onError: e => {
      toast.error('Failed to update product')
    },
    onCompleted: () => {
      toast.success('Product updated successfully')
      // Reset form state to clear touched/dirty flags while keeping current values
      const currentValues = form.getValues()
      form.reset(currentValues)
    },
    refetchQueries: ['FindManyProductContainer'],
  })

  function onSubmit(values: z.infer<typeof formSchema>) {
    updateProduct({
      variables: {
        id,
        reference: values.reference,
        advanced_product_profiles: values.advanced_product_profiles.map(p => ({
          id: p.id,
          name: p.name,
          long_product_name: p.long_product_name,
          original_product_name: p.original_product_name,
          description: p.description,
          language: p.language,
          meta_information: {
            id: p.meta_information?.id,
            permalink: p.meta_information.permalink,
            meta_title: p.meta_information.meta_title || '',
            meta_description: p.meta_information.meta_description || '',
          },
        })),
      },
    })
  }

  useEffect(() => {
    if (productData?.advanced_product?.advanced_product_profiles) {
      const existingProfiles =
        productData.advanced_product.advanced_product_profiles || []

      const formValues = {
        reference: productData.reference ?? '',
        advanced_product_profiles: allLanguages.map(lang => {
          // Find existing data for this language
          const existingProfile = existingProfiles.find(
            p => p.language === lang
          )

          if (existingProfile) {
            // Use existing data
            return {
              id: existingProfile.id,
              name: existingProfile.name,
              long_product_name: existingProfile.long_product_name ?? '',
              original_product_name:
                existingProfile.original_product_name ?? '',
              description: existingProfile.description ?? '',
              language: existingProfile.language,
              meta_information: {
                id: existingProfile.meta_information?.id,
                permalink: existingProfile.meta_information?.permalink ?? '',
                meta_title: existingProfile.meta_information?.meta_title ?? '',
                meta_description:
                  existingProfile.meta_information?.meta_description ?? '',
              },
            }
          } else {
            // Create new empty profile for this language
            return {
              id: undefined,
              name: '',
              long_product_name: '',
              original_product_name: '',
              description: '',
              language: lang,
              meta_information: {
                id: undefined,
                permalink: '',
                meta_title: '',
                meta_description: '',
              },
            }
          }
        }),
      }
      form.reset(formValues)
    }
  }, [productData, form.reset, allLanguages])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  // Helper function to create URL-friendly permalink
  const createPermalink = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .trim()
      .substring(0, 50) // Limit length
  }

  // Handler to sync field values across all languages when setting is "make_all_same"
  const handleFieldChange = (
    fieldName: 'name' | 'description',
    value: string
  ) => {
    const translateAction =
      fieldName === 'name' ? nameTranslateAction : descriptionTranslateAction

    if (translateAction === 'make_all_same') {
      // Update all language profiles with the same value
      allLanguages.forEach((_, langIndex) => {
        form.setValue(
          `advanced_product_profiles.${langIndex}.${fieldName}`,
          value,
          {
            shouldDirty: true,
            shouldTouch: true,
          }
        )

        // If description is being set, also set default values for name and permalink if empty
        if (fieldName === 'description' && value.trim()) {
          const currentName = form.getValues(
            `advanced_product_profiles.${langIndex}.name`
          )
          const currentPermalink = form.getValues(
            `advanced_product_profiles.${langIndex}.meta_information.permalink`
          )

          // Set default name if empty (take from default language)
          if (!currentName || currentName.trim() === '') {
            const defaultLanguageName = form.getValues(
              `advanced_product_profiles.0.name`
            )
            if (defaultLanguageName && defaultLanguageName.trim() !== '') {
              form.setValue(
                `advanced_product_profiles.${langIndex}.name`,
                defaultLanguageName,
                {
                  shouldDirty: true,
                  shouldTouch: true,
                }
              )
            }
          }

          // Set default permalink if empty (take from default language)
          if (!currentPermalink || currentPermalink.trim() === '') {
            const defaultLanguagePermalink = form.getValues(
              `advanced_product_profiles.0.meta_information.permalink`
            )
            if (
              defaultLanguagePermalink &&
              defaultLanguagePermalink.trim() !== ''
            ) {
              form.setValue(
                `advanced_product_profiles.${langIndex}.meta_information.permalink`,
                defaultLanguagePermalink,
                {
                  shouldDirty: true,
                  shouldTouch: true,
                }
              )
            }
          }
        }
      })
    }
  }

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <div className="flex justify-between items-center">
          <SheetTitle>Advanced Product</SheetTitle>
          <LanguageSwitcher />
        </div>
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              <InputFieldContainer
                label="Product Name"
                description="The name of your product that will be displayed to customers."
                control={form.control}
                name={`advanced_product_profiles.${languageId}.name`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input
                        placeholder="Enter product name"
                        {...field}
                        onChange={e => {
                          field.onChange(e)
                          handleFieldChange('name', e.target.value)
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Long Product Name"
                description="A longer, more descriptive name for your product."
                control={form.control}
                name={`advanced_product_profiles.${languageId}.long_product_name`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Enter long product name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Original Product Name"
                description="The original name of the product, useful for reference."
                control={form.control}
                name={`advanced_product_profiles.${languageId}.original_product_name`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input
                        placeholder="Enter original product name"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Description"
                description="Detailed description of your product including key features and benefits."
                control={form.control}
                name={`advanced_product_profiles.${languageId}.description`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Textarea
                        placeholder="Enter product description"
                        className="min-h-[100px] resize-none"
                        {...field}
                        onChange={e => {
                          field.onChange(e)
                          handleFieldChange('description', e.target.value)
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Permalink"
                description="URL-friendly version of the product name."
                control={form.control}
                name={`advanced_product_profiles.${languageId}.meta_information.permalink`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Enter permalink" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Meta Title"
                description="Title used for SEO purposes."
                control={form.control}
                name={`advanced_product_profiles.${languageId}.meta_information.meta_title`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Enter meta title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Meta Description"
                description="Description used for SEO purposes."
                control={form.control}
                name={`advanced_product_profiles.${languageId}.meta_information.meta_description`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Textarea
                        placeholder="Enter meta description"
                        className="min-h-[100px] resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Separator className="my-4" />

              <InputFieldContainer
                label="Reference"
                description="A reference identifier for the product."
                control={form.control}
                name="reference"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Enter product reference" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-2">
                <Button type="submit" disabled={loadingProductData}>
                  Save Changes
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  )
}
